import { Spacing } from '@persona-id/design-tokens';
import { range } from 'lodash/fp';
import { ReactElement, ReactNode, useCallback, useRef, useState } from 'react';
import { Document, Page, PageProps, PasswordResponses, pdfjs } from 'react-pdf';
import styled, { css } from 'styled-components';

import { BackgroundColors } from 'design-system/theme';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

interface StyledPageProps extends PageProps {
  background?: keyof BackgroundColors | null;
}

const StyledPage = styled(Page)<StyledPageProps>`
  display: flex;
  justify-content: center;
  background: ${({ theme, background = 'secondary' }) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return theme.color.background[background];
  }};
  &:first-child {
    margin-top: 0;
  }
  &:last-child {
    margin-bottom: 0;
  }
  margin: ${Spacing[2]} 0;
`;

interface AnnotationTransformStylingContainerProps {
  rotate?: number;
  pageWidth?: number;
  pageHeight?: number;
}

const AnnotationTransformStylingContainer = styled('div')<AnnotationTransformStylingContainerProps>`
  ${({ rotate, pageWidth, pageHeight }) => {
    if (rotate == null || rotate === 0 || pageWidth == null || pageHeight == null) {
      return css`
        height: 100%;
        transform-origin: top center;
      `;
    } else if (rotate === 90) {
      return css`
        width: ${pageWidth}px;
        height: ${(pageWidth * pageWidth) / pageHeight}px;
        transform-origin: bottom left;
        transform: translateY(-100%) rotate(${rotate}deg) scale(${pageHeight / pageWidth});
      `;
    } else if (rotate === 180) {
      return css`
        height: 100%;
        transform-origin: bottom center;
        transform: translateY(-100%) rotate(${rotate}deg);
      `;
    } /* rotate 270 */ else {
      return css`
        width: ${pageWidth}px;
        height: ${(pageWidth * pageWidth) / pageHeight}px;
        transform-origin: bottom right;
        transform: translateY(-100%) rotate(${rotate}deg) scale(${pageHeight / pageWidth});
      `;
    }
  }}
`;

// react-pdf v9+ is having trouble loading the Promise.withResolvers polyfill to the new worker, this works
// on the legacy worker though. Issue: https://github.com/wojtekmaj/react-pdf/issues/1811#issuecomment-2210333258
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const Pdf = ({
  src,
  onFileLoad,
  onPageLoad,
  onError,
  height,
  width,
  background,
  pagesToShow,
  fileRef,
  fileStyleRef,
  renderPdfTextLayer = true,
  renderAnnotationLayer = true,
  rotate,
  scale,
  annotations,
  loading,
}: {
  src: string;
  onFileLoad: (numPages?: number) => void;
  onPageLoad: () => void;
  onError: () => void;
  height?: number;
  width?: number;
  background?: keyof BackgroundColors;
  pagesToShow?: number;
  fileRef?: Array<HTMLImageElement | HTMLCanvasElement>;
  fileStyleRef?: Array<HTMLImageElement | HTMLDivElement>;
  renderPdfTextLayer?: boolean;
  renderAnnotationLayer?: boolean;
  rotate?: number;
  scale?: number;
  loading?: ReactElement | string;
  annotations?: ReactNode[];
}) => {
  const [numPages, setNumPages] = useState<number>(1);
  const [pageWidth, setPageWidth] = useState<number>();
  const [pageHeight, setPageHeight] = useState<number>();
  const [showDocument, setShowDocument] = useState<boolean>(true);
  const pageRef = useRef<HTMLImageElement | HTMLDivElement>(null);

  const setPageSizes = () => {
    setPageWidth(pageRef.current?.clientWidth);
    setPageHeight(pageRef.current?.clientHeight);
  };

  // https://github.com/wojtekmaj/react-pdf/issues/581#issuecomment-654741296
  const onPassword = useCallback((callback, reason) => {
    const callbackProxy = (password: string | null) => {
      // Cancel button handler
      if (password == null) {
        setShowDocument(false);
      } else {
        callback(password);
      }
    };

    switch (reason) {
      case PasswordResponses.NEED_PASSWORD: {
        const password = prompt('Enter the password to open this PDF file.');
        callbackProxy(password);
        break;
      }
      case PasswordResponses.INCORRECT_PASSWORD: {
        const password = prompt('Invalid password. Please try again.');
        callbackProxy(password);
        break;
      }
      default:
    }
  }, []);

  if (!showDocument) {
    return null;
  }

  return (
    <Document
      file={src}
      onLoadSuccess={({ numPages }) => {
        setNumPages(numPages ?? 0);
        onFileLoad(numPages ?? 0);
      }}
      onLoadError={onError}
      onPassword={onPassword}
      rotate={rotate}
      loading={loading}
    >
      {range(0, pagesToShow ?? numPages).map((index) => (
        <div key={`page_${index + 1}`} style={{ position: 'relative' }}>
          <StyledPage
            pageNumber={index + 1}
            height={height}
            width={width}
            background={background}
            loading=''
            inputRef={(x) => {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              fileStyleRef ? (fileStyleRef[index] = x) : null;
              if (index === 0) {
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                pageRef.current = x;
              }
            }}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            canvasRef={(x) => (fileRef ? (fileRef[index] = x) : null)}
            onRenderSuccess={() => {
              onPageLoad();
              setPageSizes();
            }}
            renderTextLayer={renderPdfTextLayer}
            renderAnnotationLayer={renderAnnotationLayer}
            scale={scale}
          />
          <div style={{ position: 'absolute', top: 0, height: '100%', width: '100%' }}>
            <AnnotationTransformStylingContainer
              rotate={rotate}
              pageWidth={pageWidth}
              pageHeight={pageHeight}
            >
              {annotations && annotations.length > index && annotations[index]}
            </AnnotationTransformStylingContainer>
          </div>
        </div>
      ))}
    </Document>
  );
};

export default Pdf;
